/**
 * The write rules' hooks (`writes.ts`): a page's one-write-at-a-time guard,
 * and its timers (the reads again after a lost answer), cleared when the page goes.
 */
import * as React from "react";

import { oneWriteAtATime, type OneWrite } from "@/features/writes/writes";

/** This page's {@link OneWrite}, the same for as long as it is on screen. */
export function useOneWrite(): OneWrite {
  return React.useState(oneWriteAtATime)[0];
}

/** Run something after a delay, unless the page has gone by then: the returned
 *  function keeps its identity; every timer still pending is cleared on unmount. */
export function useTimers(): (delay: number, run: () => void) => void {
  const timers = React.useRef(new Set<ReturnType<typeof setTimeout>>());
  React.useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);
  return React.useCallback((delay: number, run: () => void) => {
    const id = setTimeout(() => {
      timers.current.delete(id);
      run();
    }, delay);
    timers.current.add(id);
  }, []);
}
